import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Upload,
  Edit,
  Loader2,
  FileText,
  CheckCircle,
  Clock,
  Download,
  ShieldCloseIcon,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "@/utils/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import ProvisionalSanctionForm from "../../../components/district_admin/ProvisionalSanctionForm";
import ReviewSection from "../../../components/common/ReviewSection";
import FileReviewSection from "../../../components/common/FileReviewSection";
import DynamicReviewTable from "../../../components/common/DynamicReviewTable";
import FieldInspectionForm from "../../../components/district_admin/FieldInspectionForm";
import StatusBadge from "../../../components/common/StatusBadge";

const OldConventionalDetails = ({ application, view_page = false }) => {
  const [data, setData] = useState();
  const [processedPurchaseDetails, setProcessedPurchaseDetails] = useState([]);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [adminApproveModalOpen, setAdminApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveFile, setApproveFile] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [provisionalSanctionAmount, setProvisionalSanctionAmount] = useState(
    application?.sanction?.sanction_order_amount
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploads_array = [
    { name: "Profile Photo", value: "photo" },
    { name: "Aadhaar Copy", value: "aadhaar_copy" },
    { name: "Electricity Bill", value: "eb_bill" },
    { name: "Uydog Aadar Copy", value: "udyog_aadhaar_copy" },
    { name: "SSI Registration", value: "ssi_copy" },
    { name: "Invoice Copy", value: "invoice_file" },
    { name: "Bank Passbook", value: "bank_passbook" },
    { name: "Scheme Documents", value: "scheme_documents" },
    { name: "Applicant Signature", value: "applicant_signature" },
    { name: "Export Oriented Copy", value: "export_oriented_copy" },
    { name: "Cluster Approach Copy", value: "cluster_approach_copy" },
    { name: "Opt Out Declaration Copy", value: "opt_out_declaration_copy" },
    { name: "Other", value: "other" },
  ];

  const tabsOrder = [
    { id: "personal", name: "Personal Details" },
    { id: "loom", name: "Beneficiary and Loom Details" },
    { id: "modernization", name: "Upgradation Details" },
    { id: "bank", name: "Bank Details" },
    { id: "priority", name: "Additional Details" },
    { id: "purchase_detail", name: "Purchase Details" },
    { id: "declaration", name: "Declaration" },
    { id: "all_documents", name: "All Uploaded Documents" },
    {
      id: "m_panel_list",
      name: "Verified with M-Panel supplier List's (13 supplier from G.O.I and List Approved by D.o.H",
    },
  ];

  const afterSanctionTabsOrder = [
    { id: "sanction_details", name: "Sanction Details" },
    { id: "after_purchase_details", name: "After Purchase Details" },
    { id: "purchase_declaration", name: "Purchase Declaration Details" },
    { id: "all_documents", name: "All Uploaded Documents" },
  ];

  const role = useSelector((state) => state.auth.user?.role) || "applicant";

  useEffect(() => {
    setData({ ...application });
    processPurchaseDetails(application);
  }, [application]);

  const processPurchaseDetails = (appData) => {
    if (
      !appData?.purchase?.purchase_details ||
      !appData?.loom_upgradations?.loom_purchases
    ) {
      setProcessedPurchaseDetails([]);
      return;
    }

    const processed = appData.purchase?.purchase_details.map((purchase) => {
      const correspondingLoom = appData.loom_upgradations?.loom_purchases.find(
        (loom) => loom.id === purchase.loom_id
      );

      return {
        ...purchase,
        loom_name: correspondingLoom?.loom_name || "-",
      };
    });

    setProcessedPurchaseDetails(processed);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-IN");
    } catch (error) {
      return "-";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === "0") return "₹0.00";
    return `₹${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
    })}`;
  };

  const renderFileLink = (url, label = "View Document") => {
    if (!url || url.includes("tmp\\") || url === "null" || url === "undefined")
      return "N/A";
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {label}
      </a>
    );
  };

  const openApproveModal = () => {
    if (role == "admin") {
      setAdminApproveModalOpen(true);
    } else {
      setApproveModalOpen(true);
    }
  };

  const openRejectModal = () => {
    setRejectModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      let response;

      if (role == "admin") {
        const formData = new FormData();
        formData.append("id", data?.id);
        formData.append("remarks", remarks);
        formData.append("sanction_order_amount", provisionalSanctionAmount);

        if (approveFile) {
          formData.append("approve_file", approveFile);
        }

        response = await api.post("modernization/committee/approve", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await api.post("modernization/district-admin/approve", {
          id: data?.id,
          remarks: remarks,
        });
      }

      setData(response?.data?.data);
      toast({
        title: "Approved",
        description: "Submission approved successfully",
        variant: "success",
      });
      setApproveModalOpen(false);
      setAdminApproveModalOpen(false);
      setRemarks("");
      setProvisionalSanctionAmount("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Approval failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsSubmitting(true);
      const submitData = new FormData();
      submitData?.append("remarks", remarks);
      submitData?.append("form_id", application?.id);
      let response;
      if (role == "admin") {
        response = await api.post(
          "modernization/committee/reject",
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        response = await api.post(
          "modernization/district-admin/reject",
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      }

      setData(response?.data?.data);

      toast({
        title: "Rejected",
        description: "Submission rejected successfully",
        variant: "destructive",
      });
      setRejectModalOpen(false);
      setRemarks("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Rejection failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setData({ ...application });
    processPurchaseDetails(application);
  }, [application]);

  const personalData = [
    {
      label: "Name of the Applicant / Name of the Organization",
      value: data?.applicant_name || "-",
    },
    {
      label: "Organization Type",
      value: data?.membership || data?.user?.organization_type || "-",
    },
    ...(data?.membership == "Private Firm / Partnership" ||
    data?.membership == "Proprietor"
      ? [
          {
            label: "Name of Proprietor / Partners / Managing Director",
            value: data?.name_of_proprietor || "-",
          },
        ]
      : []),
    { label: "Father Name/ Spouse Name", value: data?.father_name || "-" },
    { label: "Date of Birth", value: formatDate(data?.date_of_birth) },
    ...(data?.membership == "Private Firm / Partnership" ||
    data?.membership == "Proprietor"
      ? [
          {
            label: "Date of Incorporation",
            value: formatDate(data?.date_of_incorporation) || "-",
          },
        ]
      : []),
    { label: "Age", value: data?.age || "-" },
    { label: "Gender", value: data?.gender || "-" },
    { label: "Community", value: data?.community_info?.name || "-" },
    { label: "Contact Number", value: data?.contact_number || "-" },
    { label: "Email", value: data?.email || "-" },
    { label: "Web Address", value: data?.web_address || "-" },
    { label: "Aadhaar Number", value: data?.aadhaar_number || "-" },
    {
      label: "Residential Address",
      value: data?.residential_address || "-",
    },
    { label: "District", value: data?.district?.name || "-" },
    { label: "City", value: data?.city || "-" },
    { label: "State", value: data?.state || "-" },
    { label: "Pincode", value: data?.residential_pincode || "-" },
    ...(data?.membership == "Private Firm / Partnership" ||
    data?.membership == "Proprietor"
      ? [
          {
            label: "PAN Number",
            value: data?.pan_number || "-",
          },
          {
            label: "GST Number",
            value: data?.gst_number || "-",
          },
        ]
      : []),
  ];

  const beneficiaryAndLoomData = [
    ...(data?.membership == "Private Firm / Partnership" ||
    data?.membership == "Proprietor"
      ? [
          {
            label: "Registration Proof",
            value: data?.beneficiary_loom_details?.registration_type || "-",
          },
        ]
      : []),
    {
      label: "Loom Shed Name",
      value: data?.beneficiary_loom_details?.loom_shed_name || "-",
    },
    {
      label: "Loom Address",
      value: data?.beneficiary_loom_details?.loom_shed_address || "-",
    },
    {
      label: "Loom Pin Code",
      value: data?.beneficiary_loom_details?.loom_shed_pincode || "-",
    },
    {
      label: "EB Service SC Number",
      value: data?.beneficiary_loom_details?.eb_service_number || "-",
    },
    {
      label: "Free EB availed or Not",
      value: data?.beneficiary_loom_details?.free_eb_availed || "-",
    },
    {
      label: "Electricity Connection Name",
      value: data?.beneficiary_loom_details?.electricity_bill_owner_name || "-",
    },
    {
      label: "Employment Benefits under Saree / Dhoti or Uniform Schemes?",
      value: data?.beneficiary_loom_details?.has_other_schemes || "-",
    },
    {
      label:
        "Any Other Benefits Received from State / Central Government Schemes",
      value: data?.beneficiary_loom_details?.other_state_gov_scheme || "-",
    },
    ...(data?.beneficiary_loom_details?.other_state_gov_scheme &&
    data?.beneficiary_loom_details?.other_state_gov_scheme !=
      "None of the above"
      ? [
          {
            label: "Specify Scheme Details",
            value:
              data?.beneficiary_loom_details?.other_state_gov_scheme || "-",
          },
        ]
      : []),
    {
      label: "Premises Type",
      value: data?.beneficiary_loom_details?.premises_type || "-",
    },
    {
      label: "Name of Variety Produced",
      value:
        (data?.beneficiary_loom_details?.variety_produced &&
          data?.beneficiary_loom_details?.variety_produced.join(", ")) ||
        "-",
    },
    ...(data?.beneficiary_loom_details?.variety_produced?.includes(
      "Any Other (Specify)"
    )
      ? [
          {
            label: "Specify the Other Variety Produced If Not Mentioned",
            value: data?.beneficiary_loom_details?.other_variety || "-",
          },
        ]
      : []),
    {
      label: "Number of existing Ordinary Powerlooms Installed",
      value: data?.beneficiary_loom_details?.ordinary_powerlooms || "-",
    },
  ];

  const modernizationData = [
    {
      label: "Number of conventional looms proposed to be upgraded",
      value: data?.loom_upgradations?.no_of_looms_to_upgrade || "-",
    },
    // {
    //   label: "Total Subsidy Amount to be claimed",
    //   value: formatCurrency(data?.estimated_total_subsidy_amount) || "-",
    // },
  ];

  const bankData = [
    {
      label: "Account Holder Name",
      value: data?.bank?.account_holder_name || "-",
    },
    { label: "Account Number", value: data?.bank?.account_no || "-" },
    { label: "Account Type", value: data?.bank?.account_type || "-" },
    {
      label: "Account Operated By Individual / Joint Account",
      value: data?.bank?.account_operated_by || "-",
    },
    { label: "Bank Name", value: data?.bank?.bank_name || "-" },
    { label: "Bank Address", value: data?.bank?.bank_address || "-" },
    { label: "Branch", value: data?.bank?.branch || "-" },
    { label: "Bank District", value: data?.bank?.bank_district || "-" },
    { label: "IFSC Code", value: data?.bank?.ifsc_code || "-" },
    { label: "MICR Code", value: data?.bank?.micr_code || "-" },
  ];

  const priorityData = [
    {
      label:
        "Whether you have made any tie-up arrangement with any marketing agency / running an own industry",
      value: data?.priority?.is_tie_up_made || "-",
    },
    ...(data?.priority?.is_tie_up_made == "Yes"
      ? [
          {
            label: "Specify the Other Variety Produced If Not Mentioned",
            value:
              (data?.priority?.tie_up_types &&
                data?.priority?.tie_up_types.join(", ")) ||
              "-",
          },
        ]
      : []),
    ...(data?.priority?.tie_up_types &&
    data?.priority?.tie_up_types?.includes("Own Manufacturing Industries")
      ? [
          {
            label: "Source of Raw Material",
            value: data?.priority?.backward_raw_material_source || "-",
          },
          {
            label: "Availability of Labour",
            value: data?.priority?.backward_labour_availability || "-",
          },
        ]
      : []),
    {
      label: "Whether Engaged in Export Oriented Production",
      value: data?.priority?.export_oriented || "-",
    },
    ...(data?.priority?.export_oriented == "yes"
      ? [
          {
            label: "Type of Product Exported",
            value: data?.priority?.type_of_product_exported || "-",
          },
        ]
      : []),
    {
      label: "Business Turnover for the availed financial Year (in Rs.)",
      value: formatCurrency(data?.priority?.turnover) || "-",
    },
    {
      label: "Whether Loom Shed is Located in Cluster",
      value: data?.priority?.cluster_approach || "-",
    },
    {
      label:
        "Willingness to Switch Over to Commercial Production and Innovations",
      value: data?.priority?.willingness_to_switch || "-",
    },
    ...(data?.priority?.willingness_to_switch == "yes"
      ? [
          {
            label: "Enter Description",
            value: data?.priority?.willingness_description || "-",
          },
        ]
      : []),
    {
      label: "Whether the applicant is a New Entrepreneur",
      value: data?.priority?.entrepreneur_type || "-",
    },
    {
      label: "Whether Engaged in Value Added Varieties",
      value: data?.priority?.value_added_variety
        ? data?.priority?.value_added_variety?.join(", ")
        : "-",
    },
    ...(data?.priority?.value_added_variety?.includes("Any Other (Specify)")
      ? [
          {
            label: "Specify Other Value added varieties",
            value: data?.priority?.other_variety_specify || "-",
          },
        ]
      : []),
    {
      label:
        "Are you a weaver of powerloom co-operative society who prefer to come out from the scheme production",
      value: data?.priority?.is_opt_out_from_scheme || "-",
    },
    {
      label: "Estimated Employment generation",
      value: data?.priority?.employment_generation || "-",
    },
  ];

  const purchaseData = [
    {
      label: "Already Purchased",
      value: data?.purchase?.already_purchase || "-",
    },
    ...(data?.purchase?.already_purchase === "Yes"
      ? [
          {
            label: "Bank Loan Availed",
            value: data?.purchase?.bank_loan_availed || "-",
          },
        ]
      : []),
  ];

  const afterPurchaseData = [
    {
      label: "Bank Loan Availed",
      value: data?.purchase?.bank_loan_availed || "-",
    },
  ];

  const uploadedFiles = [
    {
      label: "Photo",
      value: renderFileLink(data?.photo, "View Photo"),
    },
    {
      label: "Aadhaar Copy",
      value: renderFileLink(data?.aadhaar_copy),
    },
    {
      label: "EB Bill",
      value: renderFileLink(
        data?.beneficiary_loom_details?.electricity_bill_attachment?.file_path
      ),
    },
    {
      label: "Udyog Aadhaar",
      value: renderFileLink(
        data?.beneficiary_loom_details?.udyog_aadhaar_copy?.file_path
      ),
    },
    {
      label: "SSI Registration",
      value: renderFileLink(data?.beneficiary_loom_details?.ssi_copy),
    },
    {
      label: "GPS Location (Geo Tag)",
      value: renderFileLink(data?.beneficiary_loom_details?.geo_tag),
    },
    {
      label: "Invoice File",
      value: renderFileLink(data?.beneficiary_loom_details?.invoice_copy),
    },
    {
      label: "Bank Passbook",
      value: renderFileLink(data?.bank?.bank_passbook),
    },
    {
      label: "Scheme Documents",
      value: renderFileLink(data?.priority?.scheme_documents),
    },
    {
      label: "Signature of the applicant",
      value: renderFileLink(data?.declaration?.applicant_signature),
    },

    {
      label: "Export Oriented Copy",
      value: renderFileLink(data?.priority?.export_oriented_copy),
    },

    {
      label: "Cluster Approach Copy",
      value: renderFileLink(data?.priority?.cluster_approach_copy),
    },
    {
      label: "Undertaking certificate for not selling",
      value: renderFileLink(data?.purchase?.purchase_detail_bond),
    },
    {
      label: "Declaration Document",
      value: renderFileLink(data?.declaration?.declaration_document),
    },
    {
      label: "DRP Copy",
      value: renderFileLink(data?.purchase?.dpr_copy),
    },
    {
      label: "Bank Loan Sanction Letter",
      value: renderFileLink(data?.purchase?.bank_loan_sanction_letter),
    },
  ];

  const renderInvoiceCopy = (url) => {
    if (
      !url ||
      url.includes("tmp\\") ||
      url === "null" ||
      url === "undefined" ||
      url.trim() === ""
    ) {
      return "N/A";
    }
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        View Invoice
      </a>
    );
  };

  const loomPurchase = [
    { column: "S.No", value: "" },
    { column: "Loom Name", value: "loom_name" },
    { column: "Serial Number", value: "serial_number" },
    { column: "Purchase Date", value: "purchase_date" },
    {
      column: "Invoice Copy",
      value: "invoice_copy",
      is_upload: true,
      renderFunction: renderInvoiceCopy,
    },
    { column: "Attachments / Kits", value: "attachments", is_array: true },
  ];

  const purchaseDetails = [
    { column: "S.No", value: "" },
    { column: "Loom Name", value: "loom_name" },
    { column: "Components", value: "attachments", is_array: true },
    { column: "Invoice Number", value: "invoice_number" },
    { column: "Invoice Date", value: "invoice_date" },
    { column: "Invoice Copy", value: "invoice_copy", is_upload: true },
  ];

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openInNewTab = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank");
  };

  const fieldVisitRemark = data?.remarks?.find(
    (remark) => remark.status === "field_visit_completed"
  );

  const afterPurchaseFieldVisitRemark = data?.remarks?.find(
    (remark) => remark.status === "after_purchase_field_visit_completed"
  );

  const districtOfficerApprove = data?.remarks?.find(
    (remark) => remark.status === "district_officer_approve"
  );

  const districtOfficerReject = data?.remarks?.find(
    (remark) => remark.status === "district_officer_reject"
  );

  const committeeApprove = data?.remarks?.find(
    (remark) => remark.status === "committee_approve"
  );

  const committeeReject = data?.remarks?.find(
    (remark) => remark.status === "committee_reject"
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">
          Upgradation of 3 year old conventional powerlooms
          <br />
          into Rapier shuttleless looms Details
        </h1>
        <div className="flex gap-2">
          <Button asChild variant="default">
            <Link to="/submissions">Back</Link>
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-end">
        <div className="flex gap-2">
          {data?.status == "field_visit_completed" && (
            <Button
              variant="default"
              onClick={() => handleDownload(data?.provisional_sanction_letter)}
            >
              Download Provisional Sanction Letter
            </Button>
          )}
          <Button
            variant="default"
            onClick={() => handleDownload(data?.application_url)}
          >
            Download Application
          </Button>
          <Button
            variant="default"
            onClick={() => handleDownload(data?.application_uploads)}
          >
            Download Application Attachments
          </Button>
          {role == "district_admin" &&
            data?.status != "district_officer_approve" &&
            data?.status != "district_officer_reject" &&
            data?.status != "committee_approve" &&
            data?.status != "committee_reject" && (
              <Button variant="destructive" onClick={() => openRejectModal()}>
                Reject Application
              </Button>
            )}
        </div>
      </div>

      <div className="sticky top-0 z-50">
        <Card className="bg-white shadow-lg border-b">
          <CardContent className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p style={{ fontSize: "20px" }}>
                <strong>Application Number:</strong> {data?.application_no}
              </p>
              <p>
                <strong>Status:</strong> <StatusBadge status={data?.status} />
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      <ReviewSection
        title="Personal Details"
        data={personalData}
        main_data={data}
        tabName="personal"
        role={role}
      />
      <ReviewSection
        title="Beneficiary and Loom Details"
        data={beneficiaryAndLoomData}
        main_data={data}
        tabName="loom"
        role={role}
      />
      <ReviewSection
        title="Upgradation Details"
        data={modernizationData}
        role={role}
        main_data={data}
        tabName="loom"
        tableData={
          <DynamicReviewTable
            title={
              "Date of Purchase of conventional Looms upgraded as Rapier Shuttleless Loom"
            }
            columns={loomPurchase}
            rows={data?.loom_upgradations?.loom_purchases || []}
          />
        }
      />
      <ReviewSection
        title="Bank Details"
        data={bankData}
        main_data={data}
        tabName="bank"
        role={role}
      />
      <ReviewSection
        title="Priority Factors"
        data={priorityData}
        main_data={data}
        tabName="priority"
        role={role}
      />
      <ReviewSection
        title="Already Purchase Details"
        data={purchaseData}
        main_data={data}
        tabName="purchase_detail"
        role={role}
        tableData={
          data?.purchase?.already_purchase === "Yes" && (
            <DynamicReviewTable
              title={"Invoice Details"}
              columns={purchaseDetails}
              rows={processedPurchaseDetails}
            />
          )
        }
      />
      <FileReviewSection
        title="Uploaded Documents"
        files={uploadedFiles}
        role={role}
      />
      {view_page && (
        <Card className="bg-white shadow-lg p-3">
          <CardHeader className="p-4 padding-bottom-3 flex flex-row items-center justify-between rounded-lg bg-teal-700 opacity-80">
            <CardTitle>Declaration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4 border-t pt-4">
              {data?.declaration?.terms_accepted ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2" />{" "}
                  <p className="font-semibold">
                    User Has Accepted the declaration
                  </p>
                </>
              ) : (
                <>
                  <ShieldCloseIcon className="h-4 w-4 text-red-600 mr-2" />{" "}
                  <p className="font-semibold">
                    User Has Not Accepted the declaration
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {(role == "district_admin" || role == "admin") && data?.is_eligible && (
        <>
          <Card className="bg-white shadow-lg p-3">
            <CardHeader className="p-4 padding-bottom-3 flex flex-row items-center justify-between rounded-lg bg-teal-700 opacity-80">
              <CardTitle>District Officer Field Inspection</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.status &&
              data?.status === "submitted_for_provisional_sanction" &&
              role == "district_admin" ? (
                <FieldInspectionForm
                  formType="modernization"
                  tabsOrder={tabsOrder}
                  form_id={data?.id}
                  setData={setData}
                  loom_details={data?.loom_upgradations?.loom_purchases ?? []}
                  uploads_array={uploads_array}
                  application={data}
                  role={role}
                />
              ) : fieldVisitRemark ? (
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">
                        Field Inspection Completed
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-3">Inspected By</p>
                        <p className="font-medium">
                          {fieldVisitRemark.user_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">Inspection Date</p>
                        <p className="font-medium">
                          {new Date(
                            fieldVisitRemark.remarks_date
                          ).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">GPS Location</p>
                        <a
                          href={fieldVisitRemark?.geo_tag}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Provisional Sanction looms */}
                  {fieldVisitRemark.looms_for_sanction &&
                    fieldVisitRemark.looms_for_sanction.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          Looms recommended for provisional sanction
                        </h4>
                        <div className="bg-gray-50 p-4 rounded border">
                          <div className="flex flex-wrap gap-5">
                            {fieldVisitRemark.looms_for_sanction.map((item) => (
                              <span
                                key={item.id}
                                className="inline-flex items-center px-4 py-2 rounded-sm text-lg font-medium bg-blue-100 text-blue-800 border border-blue-200"
                              > 
                                {item.loom?.loom_name ||
                                  `Loom ID: ${item.loom_id}`}
                              </span>
                            ))}
                          </div>

                          {/* Optional: Descriptive text if needed */}
                          {!fieldVisitRemark.looms_for_sanction.length && (
                            <p className="text-sm text-gray-500 italic">
                              No looms recommended.
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Tab Checklist */}
                  {fieldVisitRemark.tabs &&
                    fieldVisitRemark.tabs.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold">
                            Tab Verification Status
                          </h4>
                          <span className="text-sm text-gray-600">
                            {
                              fieldVisitRemark.tabs.filter(
                                (t) => t.status === 1
                              ).length
                            }{" "}
                            / {fieldVisitRemark.tabs.length} Completed
                          </span>
                        </div>
                        <div className="space-y-2">
                          {fieldVisitRemark.tabs.map((tab) => {
                            const tabName =
                              tabsOrder?.find((t) => t.id === tab.tab_name)
                                ?.name || tab.tab_name.toUpperCase();
                            const isCompleted = tab.status === 1;
                            return (
                              <div
                                key={tab.tab_name}
                                className={`flex items-center justify-between p-3 border rounded ${
                                  isCompleted
                                    ? "bg-green-50 border-green-200"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isCompleted ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <Clock className="h-4 w-4 text-gray-400" />
                                  )}
                                  <span
                                    className={`text-sm font-medium ${
                                      isCompleted
                                        ? "text-green-800"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {tabName}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-600">
                                  {isCompleted ? "Verified" : "Pending"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Inspection Remarks */}
                  {fieldVisitRemark.description && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-2">Inspection Remarks</h4>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {fieldVisitRemark.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents */}
                  {fieldVisitRemark.files &&
                    fieldVisitRemark.files.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          Uploaded Documents
                        </h4>
                        <div className="space-y-2">
                          {fieldVisitRemark.files.map((file, index) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-blue-600" />
                                <span className="text-sm font-medium">
                                  {file.upload_type || `Document ${index + 1}`}
                                </span>
                              </div>
                              <a
                                href={file.upload_copy}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <Download className="h-4 w-4" />
                                Download
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p className="text-gray-500">
                  No field inspection record found
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg p-3">
            <CardHeader className="flex flex-row justify-between items-center p-4 rounded-lg opacity-80 padding-bottom-3 bg-teal-700">
              <CardTitle>Provisional Sanction</CardTitle>
              {data?.status == "field_visit_completed" && (
                <Button
                  variant="default"
                  onClick={() =>
                    handleDownload(data?.provisional_sanction_letter)
                  }
                >
                  Download Provisional Sanction Letter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {data?.status &&
              data?.status === "field_visit_completed" &&
              role == "district_admin" ? (
                <ProvisionalSanctionForm
                  formType="modernization"
                  form_id={data?.id}
                  setData={setData}
                  application={data}
                />
              ) : data?.sanction_details ? (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Order No.
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction_details.sanction_order_no}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Order Date
                      </label>
                      <p className="text-base font-semibold">
                        {new Date(
                          data.sanction_details.sanction_order_date
                        ).toLocaleDateString()}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Amount
                      </label>
                      <p className="text-base font-semibold">
                        ₹{data.sanction_details.sanction_order_amount}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        District Officer
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction_details.district_officer?.name}
                      </p>
                    </div>
                  </div>

                  {data.sanction_details.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Description
                      </label>
                      <p className="text-base">
                        {data.sanction_details.description}
                      </p>
                    </div>
                  )}

                  {data.sanction_details.sanctioned_copy && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanctioned Copy
                      </label>
                      <div className="mt-2">
                        <a
                          href={data.sanction_details.sanctioned_copy}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          Download Sanction Copy
                        </a>
                      </div>
                    </div>
                  )}

                  {data.sanction_details.is_user_accept !== null && (
                    <div className="pt-4 border-t">
                      <label className="text-sm font-medium text-gray-500">
                        User Acceptance Status
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction_details.is_user_accept ? (
                          <span className="text-green-600">Accepted</span>
                        ) : (
                          <span className="text-red-600">Not Accepted</span>
                        )}
                      </p>
                      {data.sanction_details.user_accept_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          on{" "}
                          {new Date(data?.sanction_details.user_accept_date)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, " - ")}
                          {"  "}
                          {new Date(
                            data?.sanction_details.user_accept_date
                          ).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No sanction details available</p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {data?.sanction_details?.status == "provisional_sanction_recommended" &&
        data?.purchase?.already_purchase === "No" &&
        data?.purchase?.bank_loan_availed && (
          <ReviewSection
            title="Already Purchase Details"
            data={afterPurchaseData}
            main_data={data}
            tabName="purchase_detail"
            tableData={
              <DynamicReviewTable
                title={"Invoice Details"}
                columns={purchaseDetails}
                rows={processedPurchaseDetails}
              />
            }
          />
        )}

      {data?.status &&
        data?.status === "submitted_for_approval" &&
        data?.purchase?.already_purchase === "No" && (
          <Card className="bg-white shadow-lg p-3">
            <CardHeader className="p-4 padding-bottom-3 rounded-lg bg-teal-700 opacity-80 flex flex-row items-center justify-between">
              <CardTitle>After Purchase Declaration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4 border-t pt-4">
                {data?.purchase_declaration?.terms_accepted ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />{" "}
                    <p className="font-semibold">
                      User Has Accepted the declaration
                    </p>
                  </>
                ) : (
                  <>
                    <ShieldCloseIcon className="h-4 w-4 text-red-600 mr-2" />{" "}
                    <p className="font-semibold">
                      User Has Not Accepted the declaration
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

      {(role == "district_admin" || role == "admin") &&
        data?.purchase?.already_purchase == "No" && (
          <Card className="bg-white shadow-lg p-3">
            <CardHeader className="p-4 padding-bottom-3 flex flex-row items-center justify-between rounded-lg bg-teal-700 opacity-80">
              <CardTitle>
                District Officer After Purchase Field Inspection
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {data?.status &&
              data?.status === "submitted_for_approval" &&
              role == "district_admin" ? (
                <FieldInspectionForm
                  formType="modernization"
                  tabsOrder={afterSanctionTabsOrder}
                  after_purchase={true}
                  form_id={data?.id}
                  setData={setData}
                  uploads_array={uploads_array}
                  application={data}
                />
              ) : afterPurchaseFieldVisitRemark ? (
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">
                        After Purchase Field Inspection Completed
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-3">Inspected By</p>
                        <p className="font-medium">
                          {afterPurchaseFieldVisitRemark.user_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">Inspection Date</p>
                        <p className="font-medium">
                          {new Date(
                            afterPurchaseFieldVisitRemark.remarks_date
                          ).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">GPS Location</p>
                        <a
                          href={afterPurchaseFieldVisitRemark?.geo_tag}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Tab Checklist */}
                  {afterPurchaseFieldVisitRemark.tabs &&
                    afterPurchaseFieldVisitRemark.tabs.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold">
                            Tab Verification Status
                          </h4>
                          <span className="text-sm text-gray-600">
                            {
                              afterPurchaseFieldVisitRemark.tabs.filter(
                                (t) => t.status === 1
                              ).length
                            }{" "}
                            / {afterPurchaseFieldVisitRemark.tabs.length}{" "}
                            Completed
                          </span>
                        </div>
                        <div className="space-y-2">
                          {afterPurchaseFieldVisitRemark.tabs.map((tab) => {
                            const tabName =
                              tabsOrder?.find((t) => t.id === tab.tab_name)
                                ?.name || tab.tab_name.toUpperCase();
                            const isCompleted = tab.status === 1;
                            return (
                              <div
                                key={tab.tab_name}
                                className={`flex items-center justify-between p-3 border rounded ${
                                  isCompleted
                                    ? "bg-green-50 border-green-200"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isCompleted ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <Clock className="h-4 w-4 text-gray-400" />
                                  )}
                                  <span
                                    className={`text-sm font-medium ${
                                      isCompleted
                                        ? "text-green-800"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {tabName}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-600">
                                  {isCompleted ? "Verified" : "Pending"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Inspection Remarks */}
                  {afterPurchaseFieldVisitRemark.description && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-2">Inspection Remarks</h4>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {afterPurchaseFieldVisitRemark.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents */}
                  {afterPurchaseFieldVisitRemark.files &&
                    afterPurchaseFieldVisitRemark.files.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          Uploaded Documents
                        </h4>
                        <div className="space-y-2">
                          {afterPurchaseFieldVisitRemark.files.map(
                            (file, index) => (
                              <div
                                key={file.id}
                                className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm font-medium">
                                    {file.upload_type ||
                                      `Document ${index + 1}`}
                                  </span>
                                </div>
                                <a
                                  href={file.upload_copy}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <Download className="h-4 w-4" />
                                  Download
                                </a>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p className="text-gray-500">
                  No field inspection record found
                </p>
              )}
            </CardContent>
          </Card>
        )}

      {districtOfficerApprove && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>District Officer Approval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-green-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">
                    Approval Completed
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Approved By</p>
                    <p className="font-medium">
                      {districtOfficerApprove.user_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Approval Date</p>
                    <p className="font-medium">
                      {new Date(districtOfficerApprove.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        districtOfficerApprove.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {districtOfficerApprove.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Approval Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {districtOfficerApprove.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {districtOfficerReject && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>District Officer Reject</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-blue-900">
                    Application Rejected
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Rejected By</p>
                    <p className="font-medium">
                      {districtOfficerReject.user_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Rejected Date</p>
                    <p className="font-medium">
                      {new Date(districtOfficerReject.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        districtOfficerReject.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {districtOfficerReject.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Rejected Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {districtOfficerReject.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {committeeApprove && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Approval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-green-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">
                    Approval Completed
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Approved By</p>
                    <p className="font-medium">{committeeApprove.user_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Approval Date</p>
                    <p className="font-medium">
                      {new Date(committeeApprove.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        committeeApprove.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {committeeApprove.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Approval Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {committeeApprove.description}
                    </p>
                  </div>
                </div>
              )}

              {committeeApprove.approve_file && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Approval Document</h4>
                  <div className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">Approval File</span>
                    </div>
                    <button
                      onClick={() =>
                        openInNewTab(committeeApprove.approve_file)
                      }
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {committeeReject && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Reject</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">
                    Application Rejected
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Rejected By</p>
                    <p className="font-medium">{committeeReject.user_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Rejected Date</p>
                    <p className="font-medium">
                      {new Date(committeeReject.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        committeeReject.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {committeeReject.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Rejected Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {committeeReject.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {role == "district_admin" &&
        ((data?.purchase?.already_purchase === "No" &&
          afterPurchaseFieldVisitRemark &&
          data?.status == "after_purchase_field_visit_completed") ||
          (data?.purchase?.already_purchase === "Yes" &&
            data?.status == "submitted_for_approval")) && (
          <Card>
            <CardHeader>
              <CardTitle>District Officer Approval</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-start gap-4 mt-10">
              <Button
                disabled={isSubmitting}
                variant="success"
                onClick={() => openApproveModal()}
                className="py-5"
              >
                Approve the Application and Send for Final Committee
              </Button>
              <Button
                disabled={isSubmitting}
                variant="destructive"
                onClick={() => openRejectModal()}
              >
                Reject the Application
              </Button>
            </CardContent>
          </Card>
        )}

      {role == "admin" && data?.status == "district_officer_approve" && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Approval</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-start gap-4 mt-10">
            <Button
              disabled={isSubmitting}
              variant="success"
              onClick={() => openApproveModal()}
              className="py-5"
            >
              Approve the Application
            </Button>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={() => openRejectModal()}
            >
              Reject the Application
            </Button>
          </CardContent>
        </Card>
      )}

      {role == "admin" && data?.status == "committee_approve" && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Reject Application</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-start gap-4 mt-10">
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={() => openRejectModal()}
            >
              Reject the Application
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to approve this application. Once done the
            application will be sent to the committee
          </div>
          <div className="py-4 space-y-2">
            <p>Enter remarks:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <p>Enter remarks for rejection:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isSubmitting || !remarks.trim()}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={adminApproveModalOpen}
        onOpenChange={setAdminApproveModalOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <div className="py-4">
              Are you sure you want to approve this Application. Once done the
              Application will be freezed and a sms will be sent to the concern
              Applicant.
            </div>
            <p>Provisional Sanction Amount:</p>
            <Input
              value={provisionalSanctionAmount}
              onChange={(e) => setProvisionalSanctionAmount(e.target.value)}
            />
            <p>Approve File</p>
            <Input
              type="file"
              id="approve_file"
              name="approve_file"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setApproveFile(e.target.files[0]);
                }
              }}
            />
            <p className="pt-2">Enter remarks for Approve:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setAdminApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OldConventionalDetails;
